/* eslint-disable import/no-cycle */
import axios from 'axios';
import { debounce } from 'lodash';
import {
  getDemoAccountToken,
  getToken,
  triggerSessionExpiration,
} from './shared/utils';
import { ExceptionTypes, ResponseErrorWithHandled } from './shared/types';
import { showValidationErrorNotification } from './shared/utils/errors';
import { planError } from './shared/utils/errors/plan-permission-error/plan-error';
import { showGeneralErrorToaster } from './shared/utils/errors/system-error-toaster';
import { SystemErrors } from './shared/enums/system-errors';
import { ApiStatusCode } from './shared/enums/status-codes';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

instance.interceptors.request.use(
  (config) => {
    const token = getToken();

    const demoAccountToken = getDemoAccountToken();

    if (demoAccountToken) {
      config.headers.authorization = `Bearer ${demoAccountToken}`;
    } else if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }

    return config;
  },
  (err) => Promise.reject(err),
);

const isCustomError = (err: any): boolean => !!err.response?.data?.type;

const debounceSystemError = debounce((errorMessage) => {
  showGeneralErrorToaster(errorMessage);
}, 500);

const handleSystemError = (errorMessage?: string) => {
  debounceSystemError(errorMessage || SystemErrors.InternalServerError);
};

instance.interceptors.response.use(
  (response) => response.data,
  (err): Promise<ResponseErrorWithHandled> => {
    let error: ResponseErrorWithHandled;
    if (err?.response?.status === ApiStatusCode.RateLimitExceeded) {
      error = {
        error: true,
        type: ExceptionTypes.System,
        message:
          'You have reached the limit of allowed actions. Please wait for some time before trying again.',
        isHandled: true,
      };

      handleSystemError(error.message);
      return Promise.reject(error);
    }

    // ** Handle Custom API Cancellation
    if (axios?.isCancel(err)) {
      // * Case - Only message passed to cancel token to show custom message in toaster
      if (typeof err?.message === 'string') {
        error = {
          error: true,
          type: ExceptionTypes.System,
          message: err?.message || 'Request was cancelled',
          isHandled: true,
        };
      } else {
        // * Case - Passed error message object with different keys
        const type = err?.message?.type || ExceptionTypes.System;
        const message = err?.message?.message || 'Request was cancelled';
        error = { ...err, type, message, isHandled: true };
      }
      if (error?.type !== ExceptionTypes.Silent) {
        handleSystemError(error.message);
      }
      return Promise.reject(error);
    }

    // to prevent to throw error get from backend for suspended user, will use custom error message at login-form
    if (err?.response?.data?.code === 1002) {
      error = { ...err.response.data, isHandled: true };
      return Promise.reject(error);
    }

    if (isCustomError(err)) {
      error = { ...err.response.data, isHandled: false };

      // eslint-disable-next-line default-case
      switch (error.type) {
        case ExceptionTypes.Auth:
          triggerSessionExpiration();
          break;

        case ExceptionTypes.System:
          handleSystemError();
          error.isHandled = true;
          break;

        case ExceptionTypes.Validation: {
          showValidationErrorNotification(error.messages);
          error.isHandled = true;
          break;
        }
        case ExceptionTypes.PlanPermission: {
          planError(error.code, error.message);
          error.isHandled = true;
          break;
        }
        case ExceptionTypes.General: {
          handleSystemError(error.message);
          error.isHandled = true;
          break;
        }

        case ExceptionTypes.RateLimit: {
          handleSystemError(error.message);
          error.isHandled = true;
          break;
        }

        case ExceptionTypes.Silent: {
          error.isHandled = true;
          break;
        }
      }
    } else {
      const backendData = err?.response?.data;
      const rawMessage = backendData?.message || err.message;

      error = {
        error: true,
        type: ExceptionTypes.System,
        message: rawMessage,
        isHandled: true,
        payload: backendData,
      };

      handleSystemError(error.message);
    }

    return Promise.reject(error);
  },
);

export default instance;
